import AgoraRTM, {
  RtmChannel,
  RtmClient,
  RtmMessage,
  RtmStatusCode,
} from 'agora-rtm-sdk';
import { EventEmitter } from 'events';
import { Debug } from '../../common/utils/debugLogs';

export const AGORA_APP_ID = 'fc109f41ee7b46a49e22969f8371ef69';

export class RtmEngine extends EventEmitter {
  instance: RtmClient;
  channel?: RtmChannel; // Map<channelID, channel instance>
  isLoggedIn = false;

  constructor() {
    super();
    this.instance = AgoraRTM.createInstance(AGORA_APP_ID);
    this.subscribeClientEvents();
  }

  // login RTM SDK with userId
  public login(userId: string): Promise<void> {
    return this.instance.login({
      uid: userId,
    });
  }

  // This is event from client
  private subscribeClientEvents(): void {
    // connection state changed
    this.instance.on(
      'ConnectionStateChanged',
      (
        newState: RtmStatusCode.ConnectionState,
        reason: RtmStatusCode.ConnectionChangeReason,
      ): void => {
        this.emit('ConnectionStateChanged', { newState, reason });
      },
    );

    // New message from other client
    this.instance.on(
      'MessageFromPeer',
      (message: RtmMessage, peerId: string): void => {
        this.emit('MessageFromPeer', { message, peerId });
      },
    );
  }

  // This is events from channel
  private subscribeChannelEvents(): void {
    if (this.channel === undefined) {
      return;
    }

    const channel = this.channel;
    // Emit event when new message was added to this channel
    channel.on(
      'ChannelMessage',
      (message: RtmMessage, memberId: string): void => {
        Debug.log(`RTM -> ${JSON.stringify(message)}`);
        this.emit('ChannelMessage', message, memberId);
      },
    );

    channel.on('MemberJoined', (memberId: string) => {
      this.emit('MemberJoined', memberId);
    });

    channel.on('MemberLeft', (memberId: string) => {
      this.emit('MemberLeft', memberId);
    });

    channel.on('MemberCountUpdated', (memberCount: number) => {
      this.emit('MemberLeft', memberCount);
    });
  }

  /**
   * Join RTM channel
   * @param channelId channel id to join
   */
  public joinChannel(channelId: string): void {
    if (channelId === this.channel?.channelId) {
      return;
    }

    this.channel = this.instance.createChannel(channelId);
    // join and subscribse channel event
    this.channel
      ?.join()
      .then(() => {
        console.log(`RTM -> join channel success`);
        this.subscribeChannelEvents(); // subscribse for channel events
      })
      .catch((error) => {
        console.log(`RTM -> join channel error: ${error}`);
      });
  }

  /**
   * destroy and cleanup
   */
  public destroy(): void {
    if (this.channel === undefined) {
      return;
    }
    this.channel.removeAllListeners();
    this.channel.leave();
  }
}
